import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Target, Trophy, Zap, MessageCircle, BarChart3, Shield, Sparkles, Play, CheckCircle } from 'lucide-react';
import { Particles } from '@/components/ui/particles';
import { useAuth } from '@/lib/auth';

const ExplainerPage: React.FC = () => {
  const navigate = useNavigate();
  const { authEnabled, user } = useAuth();
  const isAuthed = !authEnabled || Boolean(user);
  const trainingPath = isAuthed ? '/personas' : '/signup';
  const secondaryPath = isAuthed ? '/' : '/demo';

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#121212] via-[#1A1A1A] to-[#2A2A2A] text-white h-screen w-full flex items-center snap-start">
        <Particles
          className="absolute inset-0"
          quantity={100}
          ease={70}
          color="#ffffff"
          size={0.6}
          staticity={40}
        />
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl font-bold font-poppins bg-gradient-to-r from-white via-blue-100 to-blue-200 bg-clip-text text-transparent leading-tight">
                Kipr
              </h1>
              <p className="text-xl text-blue-100 font-inter leading-relaxed">
                Master therapist skills through AI-powered conversations
              </p>
            </div>
          
            
            <div className="space-y-4 flex flex-col items-center">
              <button
                onClick={() => navigate(trainingPath)}
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full shadow-2xl font-bold font-inter text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-purple-500/30 flex items-center space-x-3"
              >
                <Play className="w-6 h-6" />
                <span>Start Training</span>
              </button>
              
              <button
                onClick={() => navigate(secondaryPath)}
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full font-medium hover:bg-white/20 transition-all duration-300 flex items-center space-x-3"
              >
                <span>{isAuthed ? 'View Dashboard' : 'Try Demo'}</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <div className="bg-white h-screen w-full flex items-center justify-center snap-start">
        <div className="text-center space-y-6 px-6 max-w-4xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <MessageCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold font-poppins text-[#1A1A1A]">
            The Challenge
          </h2>
          <p className="text-lg text-[#555555] leading-relaxed">
            Behavior therapists need extensive practice with difficult conversations, but traditional training lacks realistic scenarios and immediate feedback.
          </p>
          <div className="space-y-4 pt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
              <p className="text-red-800 text-sm">
                <strong>Limited Practice:</strong> Few opportunities to practice challenging conversations
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
              <p className="text-red-800 text-sm">
                <strong>No Feedback:</strong> Difficult to get immediate, actionable feedback on communication skills
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
              <p className="text-red-800 text-sm">
                <strong>High Stakes:</strong> First real conversations with parents can be stressful and unpredictable
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 h-screen w-full flex items-center justify-center snap-start">
        <div className="text-center space-y-6 px-6 max-w-4xl">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold font-poppins text-[#1A1A1A]">
            The Solution
          </h2>
          <p className="text-lg text-[#555555] leading-relaxed">
            Kipr provides realistic AI-powered conversations with virtual parents, colleagues and other stakeholders, giving you safe practice with immediate feedback.
          </p>
          <div className="space-y-4 pt-4">
            <div className="bg-white border border-blue-200 rounded-lg p-4 text-left shadow-sm">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-[#1A1A1A] text-sm">
                  <strong>Realistic Scenarios:</strong> Practice with AI personas based on real parent personalities
                </p>
              </div>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-4 text-left shadow-sm">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-[#1A1A1A] text-sm">
                  <strong>Instant Feedback:</strong> Get detailed performance reports after each conversation
                </p>
              </div>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-4 text-left shadow-sm">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-[#1A1A1A] text-sm">
                  <strong>Safe Environment:</strong> Build confidence without real-world consequences
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-white h-screen w-full flex items-center justify-center snap-start">
        <div className="text-center space-y-8 px-6 max-w-4xl">
          <div className="space-y-4">
            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold font-poppins text-[#1A1A1A]">
              How It Works
            </h2>
          </div>
          
          <div className="space-y-6">
            <div className="text-left">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">Choose Your Scenario</h3>
                  <p className="text-[#555555] text-sm leading-relaxed">
                    Select from various training scenarios like intake consultations, progress updates, or crisis management.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-left">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">Practice the Conversation</h3>
                  <p className="text-[#555555] text-sm leading-relaxed">
                    Engage in realistic voice conversations with AI personas that respond like real parents or colleagues.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-left">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">Get Detailed Feedback</h3>
                  <p className="text-[#555555] text-sm leading-relaxed">
                    Receive comprehensive reports on your communication skills, empathy, and clinical accuracy.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-100 h-screen w-full flex items-center justify-center snap-start">
        <div className="text-center space-y-8 px-6 max-w-4xl">
          <div className="space-y-4">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold font-poppins text-[#1A1A1A]">
              Key Features
            </h2>
          </div>
          
          <div className="grid gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-200">
              <div className="flex items-center space-x-3 mb-3">
                <Users className="w-6 h-6 text-purple-600" />
                <h3 className="font-semibold text-[#1A1A1A]">Realistic Personas</h3>
              </div>
              <p className="text-[#555555] text-sm leading-relaxed text-left">
                Train with AI personas modeled after real parent personalities, from analytical to emotional responders.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-200">
              <div className="flex items-center space-x-3 mb-3">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <h3 className="font-semibold text-[#1A1A1A]">Performance Analytics</h3>
              </div>
              <p className="text-[#555555] text-sm leading-relaxed text-left">
                Track your progress across communication skills, empathy, accuracy, and goal achievement.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-200">
              <div className="flex items-center space-x-3 mb-3">
                <Shield className="w-6 h-6 text-green-600" />
                <h3 className="font-semibold text-[#1A1A1A]">Safe Practice Environment</h3>
              </div>
              <p className="text-[#555555] text-sm leading-relaxed text-left">
                Make mistakes and learn without impacting real client relationships or outcomes.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-200">
              <div className="flex items-center space-x-3 mb-3">
                <Trophy className="w-6 h-6 text-yellow-600" />
                <h3 className="font-semibold text-[#1A1A1A]">Skill Progression</h3>
              </div>
              <p className="text-[#555555] text-sm leading-relaxed text-left">
                Level up your abilities with structured training paths and achievement tracking.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white h-screen w-full flex items-center justify-center snap-start">
        <div className="text-center space-y-8 px-6 max-w-4xl">
          <div className="space-y-4">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold font-poppins text-[#1A1A1A]">
              Why Therapists Love Kipr
            </h2>
          </div>
          
          <div className="space-y-6 text-left">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">Build Confidence</h3>
              <p className="text-green-700 text-sm leading-relaxed">
                Practice difficult conversations repeatedly until you feel confident handling them in real life.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Improve Outcomes</h3>
              <p className="text-blue-700 text-sm leading-relaxed">
                Better communication skills lead to stronger parent relationships and improved client progress.
              </p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibent text-purple-800 mb-2">Reduce Stress</h3>
              <p className="text-purple-700 text-sm leading-relaxed">
                Feel prepared for challenging conversations instead of dreading them.
              </p>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Accelerate Learning</h3>
              <p className="text-orange-700 text-sm leading-relaxed">
                Get months of conversation practice in just hours, with immediate feedback for rapid improvement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white h-screen w-full flex items-center justify-center snap-start">
        <Particles
          className="absolute inset-0"
          quantity={60}
          ease={70}
          color="#ffffff"
          size={0.5}
          staticity={40}
        />
        <div className="relative z-10 text-center space-y-8 px-6 max-w-4xl">
          <div className="space-y-4">
            <h2 className="text-3xl font-bold font-poppins">
              Ready to Master Your Skills?
            </h2>
            <p className="text-xl text-indigo-100 leading-relaxed">
              Join therapists who are building confidence through AI-powered practice.
            </p>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={() => navigate(trainingPath)}
              className="w-full bg-white text-indigo-600 px-8 py-4 rounded-full shadow-2xl font-bold font-inter text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-white/30 flex items-center justify-center space-x-3"
            >
              <Play className="w-6 h-6" />
              <span>Start Your First Training</span>
            </button>
            
            <button
              onClick={() => navigate(secondaryPath)}
              className="w-full bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full font-medium hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-3"
            >
              <span>{isAuthed ? 'Explore Dashboard' : 'Try Demo'}</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          
          <div className="pt-4">
            <p className="text-indigo-200 text-sm">
              No credit card required • Start practicing immediately
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExplainerPage;